import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Clock,
  Flame,
  Gamepad2,
  Loader2,
  Lock,
  LogIn,
  LogOut,
  Settings,
  Shield,
  Swords,
  Target,
  TrendingUp,
  Trophy,
  Users,
  Workflow,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Avatar, BackLink, Badge, Button, RankIcon, rankColors } from '../../../../../../components/ui';
import { trpc } from '../../../../../../lib/trpc';

import type { Rank } from '../../../../../../components/ui/RankIcon';

export const Route = createFileRoute('/app/clubs/$slug/queues/$queueId/')({
  component: QueueDetailPage,
});

// ---------------------------------------------------------------------------
// ELO Sparkline (inline SVG)
// ---------------------------------------------------------------------------

function EloSparkline({ data, className = '' }: { data: number[]; className?: string }) {
  if (data.length < 2) return null;

  const width = 200;
  const height = 40;
  const padding = 2;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data.map((v, i) => {
    const x = padding + (i / (data.length - 1)) * (width - padding * 2);
    const y = height - padding - ((v - min) / range) * (height - padding * 2);
    return `${x},${y}`;
  }).join(' ');

  const trending = data[data.length - 1] >= data[0];
  const color = trending ? '#26d9b5' : '#ef4444';
  const gradId = `spark-detail-${trending ? 'up' : 'dn'}`;
  const fillPoints = `${padding},${height} ${points} ${width - padding},${height}`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className={className} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={fillPoints} fill={`url(#${gradId})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SparklineSlot({ data, loading, height = 'h-6' }: { data?: number[]; loading?: boolean; height?: string }) {
  if (loading) {
    return (
      <div className={`w-full ${height} rounded bg-surface-700/30 animate-pulse`} />
    );
  }

  if (!data || data.length < 2) {
    return (
      <div className={`w-full ${height} flex items-center justify-center`}>
        <div className="w-full h-px bg-surface-700/40" />
      </div>
    );
  }

  return <EloSparkline data={data} className={`w-full ${height}`} />;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function QueueDetailPage() {
  const { slug, queueId } = Route.useParams();
  const { data: me } = trpc.user.me.useQuery();
  const userId = me?.id;

  const { data: myClubs } = trpc.club.getMyClubs.useQuery();
  const membership = myClubs?.find((c) => c.slug === slug);
  const clubId = membership?.id;
  const isAdmin = membership?.role === 'owner' || membership?.role === 'admin';

  const { data: queue, isLoading: queueLoading } = trpc.queue.get.useQuery({ queueId });

  const { data: myStats } = trpc.clubMember.getStats.useQuery(
    { clubId: clubId ?? '', userId: userId ?? '' },
    { enabled: !!clubId && !!userId },
  );

  const queueStats = myStats?.find((s) => s.queueId === queueId);

  const { data: leaderboardData } = trpc.leaderboard.get.useQuery(
    { clubId: clubId ?? '', queueId, limit: 50 },
    { enabled: !!clubId },
  );

  const { data: sparklineData, isLoading: sparklineLoading } = trpc.clubMember.getEloSparkline.useQuery(
    { queueId },
    { enabled: !!userId, staleTime: 1000 * 60 },
  );

  const { data: matchHistory } = trpc.match.history.useQuery(
    { clubId: clubId ?? '', queueId, userId: userId ?? '', limit: 20 },
    { enabled: !!clubId && !!userId },
  );

  const { data: status } = trpc.queueEntry.status.useQuery({ queueId });
  const utils = trpc.useUtils();

  const joinMutation = trpc.queueEntry.join.useMutation({
    onSuccess: () => {
      toast.success('Joined queue');
      utils.queueEntry.status.invalidate({ queueId });
      utils.queue.get.invalidate({ queueId });
    },
    onError: (err) => {
      if (err.data?.code === 'CONFLICT') toast.error('Already in this queue');
      else if (err.data?.code === 'FORBIDDEN') toast.error(err.message);
      else toast.error('Failed to join queue');
    },
  });

  const leaveMutation = trpc.queueEntry.leave.useMutation({
    onSuccess: () => {
      toast.success('Left queue');
      utils.queueEntry.status.invalidate({ queueId });
      utils.queue.get.invalidate({ queueId });
    },
    onError: () => toast.error('Failed to leave queue'),
  });

  const isInQueue = status?.isInQueue ?? false;
  const isPending = joinMutation.isPending || leaveMutation.isPending;

  if (queueLoading || !clubId) {
    return <QueueDetailSkeleton slug={slug} />;
  }

  if (!queue) {
    return (
      <div className="max-w-[1400px] mx-auto p-4 md:p-6">
        <BackLink to="/app/clubs/$slug/queues" params={{ slug }} className="mb-6">
          Back to Queues
        </BackLink>
        <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
          <h2 className="text-xl font-display font-bold text-white mb-2">Queue not found</h2>
          <p className="text-sm text-surface-400">This queue may have been removed.</p>
        </div>
      </div>
    );
  }

  const teamSize = queue.teamSize ?? (queue.matchSize && queue.teamCount ? Math.floor(queue.matchSize / queue.teamCount) : null);

  return (
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 space-y-6">
      {/* Header */}
      <div>
        <BackLink to="/app/clubs/$slug/queues" params={{ slug }} className="mb-4">
          Back to Queues
        </BackLink>

        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-display font-bold text-white">{queue.name}</h1>
          {teamSize && (
            <Badge variant="subtle" size="sm">{teamSize}v{teamSize}</Badge>
          )}
          <div className="flex items-center gap-1.5 text-sm text-surface-400">
            <Users className="size-4" />
            <span className="tabular-nums font-medium">{queue.playersInQueue}</span>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {isAdmin && (
              <>
                <Link to="/app/clubs/$slug/queues/$queueId/flow-editor" params={{ slug, queueId }}>
                  <Button variant="outline" size="md" className="!px-3" title="Match Flow Editor">
                    <Workflow className="size-4" />
                  </Button>
                </Link>
                <Link to="/app/clubs/$slug/queues/$queueId/settings" params={{ slug, queueId }}>
                  <Button variant="outline" size="md" className="!px-3">
                    <Settings className="size-4" />
                  </Button>
                </Link>
              </>
            )}
            {isInQueue ? (
              <Button
                variant="outline"
                size="md"
                leftIcon={isPending ? <Loader2 className="size-4 animate-spin" /> : <LogOut className="size-4" />}
                disabled={isPending}
                onClick={() => leaveMutation.mutate({ queueId })}
                className="!border-red-500/30 !text-red-400 hover:!bg-red-500/10"
              >
                {isPending ? 'Leaving...' : 'Leave Queue'}
              </Button>
            ) : (
              <Button
                variant="primary"
                size="md"
                leftIcon={isPending ? <Loader2 className="size-4 animate-spin" /> : <LogIn className="size-4" />}
                disabled={isPending}
                onClick={() => joinMutation.mutate({ queueId })}
              >
                {isPending ? 'Joining...' : 'Join Queue'}
              </Button>
            )}
          </div>
        </div>

        {queue.description && (
          <p className="text-sm text-surface-400 mt-2">{queue.description}</p>
        )}

        {(queue.eloMin !== null || queue.eloMax !== null) && (
          <div className="flex items-center gap-1.5 text-xs text-surface-400 mt-2">
            <Shield className="size-3.5" />
            <span>ELO Range: {queue.eloMin ?? 0} – {queue.eloMax ?? '∞'}</span>
          </div>
        )}
      </div>

      {/* Two-column layout: Stats (left, sticky) | Leaderboard + History (right, scrolls) */}
      <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-6">
        {/* Stats panel - sticky on desktop */}
        <div className="space-y-4 xl:sticky xl:top-4 xl:self-start">
          <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">Your Stats</h3>
          {queueStats ? (
            <StatsPanel stats={queueStats} sparklineData={sparklineData ?? undefined} sparklineLoading={sparklineLoading} />
          ) : (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-surface-800/30 border border-surface-700/30">
              <Target className="size-5 text-surface-500 shrink-0" />
              <p className="text-xs text-surface-400">Play a match to start tracking stats.</p>
            </div>
          )}
        </div>

        {/* Right column: Leaderboard + Match History */}
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">Leaderboard</h3>
            <LeaderboardPanel items={leaderboardData?.items} currentUserId={userId} />
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">Recent Matches</h3>
            <MatchHistoryPanel matches={matchHistory?.items} userId={userId} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stats Panel
// ---------------------------------------------------------------------------

type StatsData = {
  displayElo: number;
  isPlacement: boolean;
  matchesPlayed: number;
  wins: number;
  losses: number;
  draws: number;
  winStreak: number;
  lossStreak: number;
  rank: string;
  division: number;
  position: number;
};

function StatsPanel({
  stats,
  sparklineData,
  sparklineLoading,
}: {
  stats: StatsData;
  sparklineData?: number[];
  sparklineLoading?: boolean;
}) {
  const rank = (stats.rank as Rank) ?? 'unranked';
  const division = stats.division ?? 0;
  const colors = rankColors[rank];
  const totalGames = stats.wins + stats.losses + stats.draws;
  const winRate = totalGames > 0 ? Math.round((stats.wins / totalGames) * 100) : 0;

  return (
    <div className="rounded-xl bg-surface-800/50 border border-surface-700/50 p-4 space-y-4">
      {/* Rank + ELO */}
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <div className={`absolute inset-0 rounded-full ${colors.glow} blur-lg scale-150 opacity-40`} />
          <RankIcon rank={rank} level={division} size="lg" />
        </div>
        <div>
          <div className={`text-[10px] font-bold uppercase tracking-widest ${colors.text}`}>
            {rank}{division > 0 ? ` ${['', 'I', 'II', 'III', 'IV'][division]}` : ''}
          </div>
          <div className="text-2xl font-display font-bold text-white tabular-nums">
            {stats.displayElo.toLocaleString()}
          </div>
          {stats.isPlacement ? (
            <div className="text-[10px] text-surface-500">Placement ({stats.matchesPlayed} played)</div>
          ) : stats.position && stats.position > 0 ? (
            <div className="text-xs text-brand-400 font-semibold">Rank #{stats.position}</div>
          ) : null}
        </div>
      </div>

      {/* Sparkline */}
      <SparklineSlot data={sparklineData} loading={sparklineLoading} height="h-8" />

      {/* Stat rows */}
      <div className="grid grid-cols-2 gap-3 text-center">
        <StatMini icon={<Swords className="size-3.5" />} label="Record" value={`${stats.wins}W ${stats.losses}L`} />
        <StatMini icon={<TrendingUp className="size-3.5" />} label="Win Rate" value={`${winRate}%`} accent={winRate >= 50} />
        <StatMini icon={<Flame className="size-3.5" />} label="Win Streak" value={String(stats.winStreak)} accent={stats.winStreak >= 3} />
        <StatMini icon={<Gamepad2 className="size-3.5" />} label="Matches" value={String(stats.matchesPlayed)} />
      </div>
    </div>
  );
}

function StatMini({ icon, label, value, accent = false }: { icon: React.ReactNode; label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-surface-800/50">
      <div className="text-surface-500">{icon}</div>
      <div className={`text-sm font-display font-bold tabular-nums ${accent ? 'text-brand-400' : 'text-white'}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-surface-500 font-medium">{label}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leaderboard Panel
// ---------------------------------------------------------------------------

type LeaderboardEntry = {
  userId: string;
  username: string;
  avatarUrl: string | null;
  displayElo: number;
  wins: number;
  losses: number;
  position: number;
  rank: string;
  division: number;
};

function LeaderboardPanel({ items, currentUserId }: { items?: LeaderboardEntry[]; currentUserId?: string }) {
  if (!items || items.length === 0) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-xl bg-surface-800/30 border border-surface-700/30">
        <Trophy className="size-5 text-surface-500 shrink-0" />
        <p className="text-xs text-surface-400">No rankings yet. Play matches to appear on the leaderboard.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-surface-800/30 border border-surface-700/30 overflow-hidden max-h-[500px] overflow-y-auto">
      <div className="grid grid-cols-[2.5rem_1fr_4rem_3.5rem] items-center px-3 py-2 text-[10px] uppercase tracking-widest text-surface-500 font-medium border-b border-surface-700/30 sticky top-0 bg-surface-800/80 backdrop-blur-sm">
        <span className="text-center">#</span>
        <span>Player</span>
        <span className="text-center">W/L</span>
        <span className="text-right">ELO</span>
      </div>

      {items.map((entry) => {
        const isCurrentUser = entry.userId === currentUserId;
        const rank = (entry.rank ?? 'unranked') as Rank;
        const colors = rankColors[rank];

        return (
          <div
            key={entry.userId}
            className={`grid grid-cols-[2.5rem_1fr_4rem_3.5rem] items-center px-3 py-2 border-b border-surface-700/20 last:border-b-0 transition-colors ${
              isCurrentUser ? 'bg-brand-500/5 border-l-2 border-l-brand-500' : 'hover:bg-surface-800/30'
            }`}
          >
            <div className="text-center">
              {entry.position <= 3 ? (
                <span className={`text-sm font-display font-bold ${
                  entry.position === 1 ? 'text-yellow-400' :
                  entry.position === 2 ? 'text-slate-300' :
                  'text-amber-600'
                }`}>
                  {entry.position}
                </span>
              ) : (
                <span className="text-sm font-display font-semibold text-surface-500 tabular-nums">{entry.position}</span>
              )}
            </div>

            <div className="flex items-center gap-2 min-w-0">
              <Avatar size="sm" variant="rounded" src={entry.avatarUrl} initials={entry.username.charAt(0)} alt={entry.username} className="shrink-0" />
              <span className={`text-sm font-medium truncate ${isCurrentUser ? 'text-brand-400' : 'text-surface-200'}`}>
                {entry.username}
                {isCurrentUser && <span className="text-xs text-surface-500 ml-1">(you)</span>}
              </span>
            </div>

            <div className="text-center text-xs text-surface-400 tabular-nums">{entry.wins}W {entry.losses}L</div>

            <div className={`text-right text-sm font-display font-bold tabular-nums ${colors.text}`}>
              {entry.displayElo.toLocaleString()}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Match History Panel
// ---------------------------------------------------------------------------

type MatchItem = {
  id: string;
  status: string;
  winningTeam: number | null;
  createdAt: string | Date;
  completedAt: string | Date | null;
  queueId: string;
};

function MatchHistoryPanel({ matches, userId }: { matches?: MatchItem[]; userId?: string }) {
  if (!matches || matches.length === 0) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-xl bg-surface-800/30 border border-surface-700/30">
        <Gamepad2 className="size-5 text-surface-500 shrink-0" />
        <p className="text-xs text-surface-400">No matches yet. Queue up to start playing.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {matches.map((match) => (
        <MatchHistoryRow key={match.id} match={match} userId={userId} />
      ))}
    </div>
  );
}

function MatchHistoryRow({ match, userId }: { match: MatchItem; userId?: string }) {
  const timeAgo = formatTimeAgo(new Date(match.completedAt ?? match.createdAt));

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-surface-800/30 border border-surface-700/20">
      <Swords className="size-4 text-surface-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-sm text-surface-300">Match</span>
      </div>
      <span className="text-xs text-surface-500">{timeAgo}</span>
    </div>
  );
}

function formatTimeAgo(date: Date): string {
  const now = Date.now();
  const diff = Math.max(0, Math.floor((now - date.getTime()) / 1000));

  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function QueueDetailSkeleton({ slug }: { slug: string }) {
  return (
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 space-y-6">
      <BackLink to="/app/clubs/$slug/queues" params={{ slug }} className="mb-4">
        Back to Queues
      </BackLink>
      <div className="h-8 w-48 rounded-lg bg-surface-700/40 animate-pulse" />
      <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-6">
        <div className="h-64 rounded-xl bg-surface-800/50 animate-pulse" />
        <div className="h-64 rounded-xl bg-surface-800/50 animate-pulse" />
      </div>
      <div className="h-48 rounded-xl bg-surface-800/50 animate-pulse" />
    </div>
  );
}
